import { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import { Play, Loader2, Sparkles, Save, CheckCircle2, Plus, Trash2, Presentation, Rocket, Zap, Building2, Package, Search, Download, X, Upload, ArrowRight, ImageIcon } from 'lucide-react';
import GoogleDrivePicker from '../components/GoogleDrivePicker';

export default function Dashboard() {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mode, setMode] = useState<'sales-demos' | 'live-deployment' | 'brand-bootstrapping' | 'ai-content'>('sales-demos');
  
  // Workflow Progress State
  const [workflowProgress, setWorkflowProgress] = useState(0);
  const [workflowStatus, setWorkflowStatus] = useState<'idle' | 'running' | 'completed' | 'error'>('idle');
  const [workflowMessage, setWorkflowMessage] = useState('');
  const [workflowResult, setWorkflowResult] = useState<any>(null);
  const [workflowResults, setWorkflowResults] = useState<any[]>([]);
  const progressInterval = useRef<NodeJS.Timeout | null>(null);
  
  // Market Research Form State
  const [campaign, setCampaign] = useState('');
  const [products, setProducts] = useState<Array<{ 
    featureDisplayName: string; 
    size: string; 
    driveFileIds: Array<{ id: string; name: string }>;
    driveAccessToken?: string;
  }>>([
    { featureDisplayName: '', size: '', driveFileIds: [] }
  ]);
  const [formErrors, setFormErrors] = useState<{
    campaign?: string;
    products?: string;
  }>({});
  
  // Brand Bootstrapping State
  const [bootstrapBrandName, setBootstrapBrandName] = useState('');
  const [bootstrapSubMode, setBootstrapSubMode] = useState<'product-brand' | 'venue-brand'>('product-brand');
  const [bootstrapProductLimit, setBootstrapProductLimit] = useState(10);
  const [bootstrapStatus, setBootstrapStatus] = useState<'idle' | 'searching' | 'preview' | 'writing' | 'completed' | 'error'>('idle');
  const [bootstrapResults, setBootstrapResults] = useState<any>(null);
  const [bootstrapMessage, setBootstrapMessage] = useState('');
  const [bootstrapProgress, setBootstrapProgress] = useState(0);
  const bootstrapInterval = useRef<NodeJS.Timeout | null>(null);
  // Accumulated products for preview (persists across multiple searches)
  const [bootstrapProducts, setBootstrapProducts] = useState<any[]>([]);
  const [bootstrapBrandColors, setBootstrapBrandColors] = useState<{ primary: string; secondary: string } | null>(null);

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
      if (bootstrapInterval.current) {
        clearInterval(bootstrapInterval.current);
      }
    };
  }, []);

  // AI Content Generator State
  const [brandName, setBrandName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImages, setGeneratedImages] = useState<string[]>([]);
  const [progress, setProgress] = useState(0);
  
  // Per-product campaign media generation state
  interface ProductGenerationState {
    isGenerating: boolean;
    progress: number;  // 0-100
    currentStep: string;  // "left", "right", "top", "banner", "done"
    stepNumber: number;  // 1-4
    generatedImages: {
      left?: string;
      right?: string;
      top?: string;
      banner?: string;
    };
    error?: string;
    savedToSheets?: boolean;  // Whether images have been pushed to sheets
  }
  const [productGenerationStates, setProductGenerationStates] = useState<Record<number, ProductGenerationState>>({});

  const addProductRow = () => {
    setProducts([...products, { featureDisplayName: '', size: '', driveFileIds: [] }]);
  };

  const removeProductRow = (index: number) => {
    if (products.length > 1) {
      setProducts(products.filter((_, i) => i !== index));
    }
  };

  const updateProduct = (index: number, field: 'featureDisplayName' | 'size', value: string) => {
    const updated = [...products];
    updated[index] = { ...updated[index], [field]: value };
    setProducts(updated);
  };

  const updateProductImages = (index: number, result: { files: Array<{ id: string; name: string }>; accessToken?: string }) => {
    const updated = [...products];
    updated[index] = { 
      ...updated[index], 
      driveFileIds: result.files,
      driveAccessToken: result.accessToken
    };
    setProducts(updated);
    console.log(`[Dashboard] Updated product ${index} with ${result.files.length} files and access token:`, !!result.accessToken);
  };

  const handleMarketResearchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Client-side validation
    const errors: typeof formErrors = {};
    if (!campaign.trim()) {
      errors.campaign = 'Campaign name is required';
    }
    
    const validProducts = products.filter(p => p.featureDisplayName.trim() && p.size.trim());
    if (validProducts.length === 0) {
      errors.products = 'At least one product with Feature Display Name and Size is required';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    setFormErrors({});
    setIsSubmitting(true);
    setWorkflowStatus('running');
    setWorkflowProgress(0);
    setWorkflowResult(null);
    setWorkflowMessage('Triggering workflow...');

    // Calculate estimated duration based on number of products
    // Realistic timing: AI research takes 10-15s per product (duplicate check + Tavily + OpenAI + Sheets write)
    // Additional products add 6-8s each (parallel processing helps significantly)
    const productCount = validProducts.length;
    const totalImageCount = validProducts.reduce((sum, p) => sum + (p.driveFileIds?.length || 0), 0);
    const baseTimeMs = 12000; // 12 seconds base for first product (duplicate check + AI research + sheets write)
    const perProductTimeMs = 7000; // 7 seconds per additional product (parallel processing helps)
    const perImageTimeMs = 1000; // 1 second per image (Drive URL fetching, done in parallel)
    const estimatedDurationMs = baseTimeMs + (productCount - 1) * perProductTimeMs + totalImageCount * perImageTimeMs;
    
    // Start progress animation with realistic timing
    const startTime = Date.now();
    let currentProgress = 0;
    
    progressInterval.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progressRatio = Math.min(elapsed / estimatedDurationMs, 0.98); // Cap at 98% until API responds
      
      // Use exponential easing to slow down more gradually as we approach 98%
      // This prevents the "freeze" feeling
      if (progressRatio < 0.85) {
        // Fast progress up to 85%
        currentProgress = progressRatio * 100;
      } else {
        // Slow down gradually from 85% to 98% with smoother easing
        const remaining = 0.98 - 0.85;
        const easedRatio = (progressRatio - 0.85) / 0.13; // Normalize to 0-1 for easing
        const slowProgress = 0.85 + remaining * (1 - Math.exp(-3 * easedRatio));
        currentProgress = slowProgress * 100;
      }
      
      // Ensure we don't exceed 98% until API responds
      currentProgress = Math.min(currentProgress, 98);
      setWorkflowProgress(currentProgress);
      
      // Update messages based on progress
      if (currentProgress <= 15) {
        setWorkflowMessage('Initializing workflow...');
      } else if (currentProgress <= 30) {
        setWorkflowMessage('Checking for existing products...');
      } else if (currentProgress <= 50) {
        setWorkflowMessage(mode === 'sales-demos' ? 'Searching UK supermarkets...' : 'Preparing data...');
      } else if (currentProgress <= 70) {
        setWorkflowMessage(mode === 'sales-demos' ? 'Extracting product data...' : 'Processing products...');
      } else if (currentProgress <= 85) {
        setWorkflowMessage('Processing results...');
      } else if (currentProgress <= 92) {
        setWorkflowMessage('Writing to Google Sheets...');
      } else if (currentProgress <= 95) {
        setWorkflowMessage('Finalizing data...');
      } else if (currentProgress <= 97) {
        setWorkflowMessage('Almost done...');
      } else {
        setWorkflowMessage('Finalizing...');
      }
    }, 50); // Update every 50ms for smoother animation

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3436/api/workflows/trigger';
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaign: campaign.trim(),
          products: validProducts.map(p => ({
            featureDisplayName: p.featureDisplayName.trim(),
            size: p.size.trim(),
            driveFileIds: p.driveFileIds.map(f => f.id), // Send array of file IDs
            driveAccessToken: p.driveAccessToken // Send access token if available
          })),
          mode: mode, // Pass mode to backend
        }),
      });

      if (!response.ok) {
        if (response.status === 0 || response.status >= 500) {
          throw new Error('Backend not available');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to trigger workflow');
      }

      const data = await response.json();
      
      // Smoothly transition to 100% before clearing interval
      // Continue progress animation to 100% over 300ms
      const finalProgressInterval = setInterval(() => {
        setWorkflowProgress((prev) => {
          if (prev >= 100) {
            clearInterval(finalProgressInterval);
            return 100;
          }
          // Increment by 2% every 50ms to reach 100% smoothly
          return Math.min(prev + 2, 100);
        });
      }, 50);
      
      // Clear main progress interval
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
        progressInterval.current = null;
      }
      
      // Set status after a brief delay to allow smooth transition
      setTimeout(() => {
        setWorkflowStatus('completed');
        setWorkflowMessage(data.message || 'Market research completed successfully!');
      }, 300);
      
      // Store all product results with source information
      if (data.results && data.results.length > 0) {
        setWorkflowResults(data.results);
        if (data.data && data.data.length > 0) {
          setWorkflowResult(data.data[0]);
        }
      } else if (data.data && data.data.length > 0) {
        setWorkflowResult(data.data[0]);
        setWorkflowResults([{ product: data.data[0]?.['Feature Display Name'], source: 'research' }]);
      }
      
      const totalRowsAdded = data.sheetsResult?.rowsAdded || 0;
      const duplicateCount = data.duplicates?.length || 0;
      const processedCount = data.results?.filter((r: any) => !r.duplicate).length || 0;
      
      let description = `Processed ${processedCount} product(s), added ${totalRowsAdded} rows to Google Sheets`;
      if (duplicateCount > 0) {
        description += `. ${duplicateCount} duplicate(s) found and skipped.`;
      }
      
      toast({
        title: 'Workflow completed',
        description: description,
      });
      
      setIsSubmitting(false);

    } catch (error: any) {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
      setWorkflowStatus('error');
      setWorkflowProgress(0);
      setWorkflowMessage('');
      setIsSubmitting(false);
      
      toast({
        title: 'Error',
        description: error.message || 'Failed to trigger workflow',
        variant: 'destructive',
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
        toast({
          title: 'Invalid file type',
          description: 'Please upload a PNG or JPG image',
          variant: 'destructive',
        });
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: 'File too large',
          description: 'Please upload an image smaller than 10MB',
          variant: 'destructive',
        });
        return;
      }
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGenerateImages = async () => {
    if (!brandName || !selectedFile) {
      toast({
        title: 'Missing information',
        description: 'Please provide both brand name and product image',
        variant: 'destructive',
      });
      return;
    }

    setIsGenerating(true);
    setProgress(0);

    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + 10;
      });
    }, 500);

    // TODO: Call actual API endpoint
    setTimeout(() => {
      clearInterval(interval);
      setProgress(100);
      setGeneratedImages([
        'https://via.placeholder.com/400x600/FF6B6B/FFFFFF?text=Campaign+1',
        'https://via.placeholder.com/400x600/4ECDC4/FFFFFF?text=Campaign+2',
        'https://via.placeholder.com/400x600/45B7D1/FFFFFF?text=Campaign+3',
      ]);
      setIsGenerating(false);
      toast({
        title: 'Success',
        description: '3 ad campaign images generated successfully',
      });
    }, 5000);
  };

  const handleSaveToSheets = async () => {
    if (generatedImages.length === 0) {
      toast({
        title: 'No images to save',
        description: 'Please generate images first',
        variant: 'destructive',
      });
      return;
    }

    // TODO: Call API to save to Google Sheets
    toast({
      title: 'Success',
      description: 'Images saved to Google Sheets',
    });
  };

  // Brand Bootstrapping Handlers
  const handleBootstrapDiscover = async () => {
    if (!bootstrapBrandName.trim()) {
      toast({
        title: 'Brand name required',
        description: 'Please enter a brand name to discover products',
        variant: 'destructive',
      });
      return;
    }

    setBootstrapStatus('searching');
    setBootstrapMessage('Initializing search...');
    setBootstrapResults(null);
    setBootstrapProgress(0);

    // Start progress simulation - discovery takes ~30-60s
    const startTime = Date.now();
    const estimatedDuration = bootstrapSubMode === 'venue-brand' ? 60000 : 45000;
    
    bootstrapInterval.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progressRatio = Math.min(elapsed / estimatedDuration, 0.95);
      
      let currentProgress: number;
      if (progressRatio < 0.8) {
        currentProgress = progressRatio * 100;
      } else {
        const slowProgress = 0.8 + (progressRatio - 0.8) * 0.5;
        currentProgress = slowProgress * 100;
      }
      
      setBootstrapProgress(Math.min(currentProgress, 95));
      
      // Update messages based on progress
      if (currentProgress <= 10) {
        setBootstrapMessage('Initializing search...');
      } else if (currentProgress <= 25) {
        setBootstrapMessage(bootstrapSubMode === 'venue-brand' 
          ? 'Finding product categories from venue...' 
          : 'Searching UK supermarkets...');
      } else if (currentProgress <= 45) {
        setBootstrapMessage(bootstrapSubMode === 'venue-brand'
          ? 'Searching supermarkets for products in each category...'
          : 'Searching Tesco, Ocado, Sainsbury\'s...');
      } else if (currentProgress <= 65) {
        setBootstrapMessage('Extracting product data with AI...');
      } else if (currentProgress <= 80) {
        setBootstrapMessage('Finding product images...');
      } else if (currentProgress <= 90) {
        setBootstrapMessage('Extracting brand colors...');
      } else {
        setBootstrapMessage('Finalizing results...');
      }
    }, 100);

    try {
      const response = await fetch('/api/workflows/bootstrap-brand', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brandName: bootstrapBrandName,
          subMode: bootstrapSubMode,
          productLimit: bootstrapProductLimit,
          campaign: bootstrapBrandName
        })
      });

      // Clear the interval
      if (bootstrapInterval.current) {
        clearInterval(bootstrapInterval.current);
      }

      const data = await response.json();

      if (data.success) {
        // Animate to 100%
        setBootstrapProgress(100);
        setBootstrapMessage(`Found ${data.products.length} products!`);
        
        // Short delay then accumulate products and show preview
        setTimeout(() => {
          // Replace products with new results (not accumulating)
          // Add unique IDs to each product for React keys
          const productsWithIds = data.products.map((p: any, i: number) => ({
            ...p,
            _id: `${Date.now()}-${i}-${Math.random().toString(36).substr(2, 9)}`
          }));
          setBootstrapProducts(productsWithIds);
          // Store brand colors from latest search
          if (data.brandColors) {
            setBootstrapBrandColors(data.brandColors);
          }
          setBootstrapResults(data);
          setBootstrapStatus('idle'); // Back to idle so user can search again or write
          toast({
            title: 'Products discovered!',
            description: `Found ${data.products.length} products from ${data.source}`,
          });
        }, 500);
      } else {
        setBootstrapStatus('error');
        setBootstrapProgress(0);
        setBootstrapMessage(data.error || 'Failed to discover products');
        toast({
          title: 'Discovery failed',
          description: data.error || 'Failed to discover products',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      if (bootstrapInterval.current) {
        clearInterval(bootstrapInterval.current);
      }
      setBootstrapStatus('error');
      setBootstrapProgress(0);
      setBootstrapMessage(error.message || 'Network error');
      toast({
        title: 'Error',
        description: error.message || 'Failed to connect to server',
        variant: 'destructive',
      });
    }
  };

  const handleBootstrapWrite = async () => {
    if (!bootstrapProducts.length) {
      toast({
        title: 'No products to write',
        description: 'Please discover products first',
        variant: 'destructive',
      });
      return;
    }

    setBootstrapStatus('writing');
    setBootstrapMessage('Preparing to process images...');
    setBootstrapProgress(0);

    // Start progress simulation - writing with image processing takes longer
    const productCount = bootstrapProducts.length;
    const estimatedDuration = productCount * 4000; // ~4s per product for image processing
    const startTime = Date.now();
    
    bootstrapInterval.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progressRatio = Math.min(elapsed / estimatedDuration, 0.95);
      
      let currentProgress: number;
      if (progressRatio < 0.85) {
        currentProgress = progressRatio * 100;
      } else {
        const slowProgress = 0.85 + (progressRatio - 0.85) * 0.3;
        currentProgress = slowProgress * 100;
      }
      
      setBootstrapProgress(Math.min(currentProgress, 95));
      
      // Estimate which product we're on
      const estimatedProduct = Math.min(Math.floor(currentProgress / 100 * productCount) + 1, productCount);
      
      // Update messages based on progress
      if (currentProgress <= 5) {
        setBootstrapMessage('Preparing to process images...');
      } else if (currentProgress <= 85) {
        setBootstrapMessage(`Processing product ${estimatedProduct}/${productCount}: Downloading image & extracting colors...`);
      } else if (currentProgress <= 92) {
        setBootstrapMessage('Writing all products to Google Sheets...');
      } else {
        setBootstrapMessage('Finalizing...');
      }
    }, 200);

    try {
      const response = await fetch('/api/workflows/bootstrap-write', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          products: bootstrapProducts,
          brandName: bootstrapBrandName,
          brandColors: bootstrapBrandColors || { primary: '#000000', secondary: '#FFFFFF' },
          campaign: bootstrapBrandName,
          processImages: true // Enable image processing
        })
      });

      // Clear the interval
      if (bootstrapInterval.current) {
        clearInterval(bootstrapInterval.current);
      }

      const data = await response.json();

      if (data.success) {
        setBootstrapProgress(100);
        setBootstrapStatus('completed');
        setBootstrapMessage(`Successfully wrote ${data.rowsAdded} products to Google Sheets!`);
        toast({
          title: 'Success!',
          description: `${data.rowsAdded} products written to Google Sheets`,
        });
      } else {
        setBootstrapStatus('error');
        setBootstrapProgress(0);
        setBootstrapMessage(data.error || 'Failed to write to sheets');
        toast({
          title: 'Write failed',
          description: data.error || 'Failed to write to Google Sheets',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      if (bootstrapInterval.current) {
        clearInterval(bootstrapInterval.current);
      }
      setBootstrapStatus('error');
      setBootstrapProgress(0);
      setBootstrapMessage(error.message || 'Network error');
      toast({
        title: 'Error',
        description: error.message || 'Failed to connect to server',
        variant: 'destructive',
      });
    }
  };

  const handleBootstrapReset = () => {
    if (bootstrapInterval.current) {
      clearInterval(bootstrapInterval.current);
    }
    setBootstrapStatus('idle');
    setBootstrapResults(null);
    setBootstrapMessage('');
    setBootstrapProgress(0);
    setBootstrapProducts([]);
    setBootstrapBrandColors(null);
  };

  const handleRemoveBootstrapProduct = (index: number) => {
    setBootstrapProducts(prev => prev.filter((_, i) => i !== index));
  };

  // State for tracking which product index is being updated
  const [productImagePickerIndex, setProductImagePickerIndex] = useState<number | null>(null);

  // Handle selecting image from Google Drive Picker for a specific product
  const handleProductImageFromDrive = async (index: number, fileId: string, accessToken?: string) => {
    try {
      toast({
        title: 'Processing image...',
        description: 'Making your image shareable',
      });

      // Call API to get shareable URL for the Drive file
      const response = await fetch('/api/workflows/drive-file-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileId, accessToken }),
      });

      const data = await response.json();

      if (data.success && data.url) {
        // Update the product's image URL
        setBootstrapProducts(prev => prev.map((product, i) => 
          i === index ? { ...product, imageUrl: data.url } : product
        ));

        toast({
          title: 'Image updated!',
          description: 'Product image has been updated from your Google Drive',
        });
      } else {
        throw new Error(data.error || 'Failed to get image URL');
      }
    } catch (error: any) {
      toast({
        title: 'Failed to update image',
        description: error.message || 'Could not process the selected image',
        variant: 'destructive',
      });
    }
    setProductImagePickerIndex(null);
  };

  /**
   * Generate all campaign media (Left, Right, Top, Banner) for a product
   * Makes ONE API call that generates all 4 images
   */
  const handleGenerateCampaignMedia = async (productIndex: number) => {
    const product = bootstrapProducts[productIndex];
    if (!product || !product.imageUrl) {
      toast({
        title: 'No product image',
        description: 'Please add a product image first',
        variant: 'destructive',
      });
      return;
    }

    // Initialize generation state for this product
    setProductGenerationStates(prev => ({
      ...prev,
      [productIndex]: {
        isGenerating: true,
        progress: 25,
        currentStep: 'generating',
        stepNumber: 1,
        generatedImages: {},
      }
    }));

    try {
      // Make ONE API call to generate ALL 4 images at once
      const response = await fetch('/api/workflows/generate-campaign-media', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productImageUrl: product.imageUrl,
          productName: product.name,
          brandName: bootstrapBrandName || product.name,
          primaryColor: product.primaryColor || bootstrapBrandColors?.primary || '#FF6600',
          secondaryColor: product.secondaryColor || bootstrapBrandColors?.secondary || '#000000',
          style: 'energetic',
          sheetName: bootstrapBrandName,
          productSize: product.size || '',
        }),
      });

      const data = await response.json();

      if (data.success && data.results) {
        // Extract the generated URLs
        const generatedImages: Record<string, string> = {};
        const positionErrors: string[] = [];
        
        for (const [key, result] of Object.entries(data.results)) {
          if ((result as any)?.success) {
            // Prefer Drive URL, but fall back to base64 data URL if Drive upload failed
            if ((result as any)?.url) {
              generatedImages[key] = (result as any).url;
            } else if ((result as any)?.base64) {
              // Create data URL from base64 for preview
              generatedImages[key] = `data:image/png;base64,${(result as any).base64}`;
              console.log(`[CAMPAIGN_MEDIA] Using base64 preview for ${key} (Drive upload failed)`);
            }
          } else if ((result as any)?.error) {
            positionErrors.push(`${key}: ${(result as any).error}`);
            console.warn(`[CAMPAIGN_MEDIA] ${key} failed: ${(result as any).error}`);
          }
        }

        // Show alert for failed positions
        if (positionErrors.length > 0) {
          toast({
            title: `⚠️ Some images failed`,
            description: positionErrors.join('\n'),
            variant: 'destructive',
          });
        }

        // Update state with all generated images - complete!
        setProductGenerationStates(prev => ({
          ...prev,
          [productIndex]: {
            isGenerating: false,
            progress: 100,
            currentStep: 'done',
            stepNumber: 4,
            generatedImages,
            error: positionErrors.length > 0 ? positionErrors.join('; ') : undefined,
          }
        }));

        toast({
          title: '✅ Images generated!',
          description: `${Object.keys(generatedImages).length}/4 campaign images ready. Click "Save to Sheets" to update Google Sheets.`,
        });
      } else if (data.error) {
        // Backend returned error
        const errorMessage = data.error || 'Unknown error';
        const failedPosition = data.failedPosition || 'unknown';
        const debugInfo = data.debug || {};
        
        console.error(`[CAMPAIGN_MEDIA] ❌ FAILED at position: ${failedPosition}`);
        console.error(`[CAMPAIGN_MEDIA] Error: ${errorMessage}`);
        console.error(`[CAMPAIGN_MEDIA] Debug info:`, debugInfo);
        
        // Build detailed user message
        let userMessage = `Failed at "${failedPosition}": ${errorMessage}`;
        
        // Add helpful hints based on error type
        if (errorMessage.includes('validation') || errorMessage.includes('access') || errorMessage.includes('bot protection')) {
          userMessage += '\n\n💡 Tip: The product image URL may be blocked. Try uploading a new image from Google Drive.';
        } else if (errorMessage.includes('rate') || errorMessage.includes('quota')) {
          userMessage += '\n\n💡 Tip: Rate limit reached. Wait 30 seconds and try again.';
        } else if (errorMessage.includes('model') || errorMessage.includes('Gemini')) {
          userMessage += '\n\n💡 Tip: Gemini service issue. Check GEMINI_API_KEY is valid.';
        }
        
        toast({
          title: `❌ Generation failed`,
          description: errorMessage,
          variant: 'destructive',
        });
        
        return;
      }
    } catch (error: any) {
      console.error('[CAMPAIGN_MEDIA] Error:', error);
      
      // Parse error for user-friendly message
      let errorMessage = error.message || 'Failed to generate campaign media';
      if (error.message?.includes('Failed to fetch')) {
        errorMessage = 'Network error - could not connect to server. Check if backend is running.';
      }
      
      setProductGenerationStates(prev => ({
        ...prev,
        [productIndex]: {
          ...prev[productIndex],
          isGenerating: false,
          progress: 0,
          error: errorMessage,
        }
      }));

      toast({
        title: '❌ Generation failed',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  /**
   * Push generated campaign images to Google Sheets
   */
  const handlePushImagesToSheets = async (productIndex: number) => {
    const product = bootstrapProducts[productIndex];
    const state = productGenerationStates[productIndex];
    
    if (!product || !state?.generatedImages) {
      toast({
        title: 'No images to save',
        description: 'Generate images first before saving to sheets',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Get the current host URL for constructing absolute URLs
      const hostUrl = window.location.origin;
      
      const response = await fetch('/api/workflows/save-campaign-images', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productName: product.name,
          productSize: product.size || '',
          sheetName: bootstrapBrandName,
          images: state.generatedImages,
          hostUrl: hostUrl, // Pass the host URL for constructing absolute URLs
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: '✅ Saved to Google Sheets!',
          description: `Campaign images for ${product.name} saved to columns P-S`,
        });
        
        // Update state to show saved
        setProductGenerationStates(prev => ({
          ...prev,
          [productIndex]: {
            ...prev[productIndex],
            savedToSheets: true,
          }
        }));
      } else {
        toast({
          title: '⚠️ Could not save to Sheets',
          description: data.hint || data.error || 'Could not save images to Google Sheets',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({
        title: '❌ Error',
        description: error.message || 'Network error',
        variant: 'destructive',
      });
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-4">
        {/* Top Header with Mode Tabs */}
        <div className="flex items-center">
          <Tabs value={mode} onValueChange={(v) => setMode(v as 'sales-demos' | 'live-deployment' | 'brand-bootstrapping' | 'ai-content')} className="w-auto">
            <TabsList className="h-12 bg-white shadow-sm border">
              <TabsTrigger 
                value="sales-demos" 
                className="text-base px-6 data-[state=active]:bg-brand-primary data-[state=active]:text-white data-[state=active]:shadow-lg"
              >
                <Presentation className="mr-2 h-5 w-5" />
                Sales Demos
              </TabsTrigger>
              <TabsTrigger 
                value="live-deployment" 
                className="text-base px-6 data-[state=active]:bg-brand-primary data-[state=active]:text-white data-[state=active]:shadow-lg"
              >
                <Rocket className="mr-2 h-5 w-5" />
                Live Deployment
              </TabsTrigger>
              <TabsTrigger 
                value="brand-bootstrapping" 
                className="text-base px-6 data-[state=active]:bg-brand-primary data-[state=active]:text-white data-[state=active]:shadow-lg"
              >
                <Zap className="mr-2 h-5 w-5" />
                Brand Bootstrapping
              </TabsTrigger>
              <TabsTrigger 
                value="ai-content" 
                className="text-base px-6 data-[state=active]:bg-brand-primary data-[state=active]:text-white data-[state=active]:shadow-lg"
              >
                <Sparkles className="mr-2 h-5 w-5" />
                AI Content Generator
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Tab Content */}
        {mode === 'brand-bootstrapping' ? (
          /* Brand Bootstrapping Mode - Discover products from brand name */
          <Card className="w-full shadow-sm border-0 bg-white">
            <CardHeader className="bg-slate-50/50">
              <CardTitle className="text-brand-black flex items-center gap-2">
                <Zap className="h-5 w-5 text-brand-primary" />
                Brand Bootstrapping
              </CardTitle>
              <CardDescription>
                Enter a brand name to automatically discover products, images, and brand colors
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="flex gap-6">
                {/* Left Column: Search Controls */}
                <div className="w-80 flex-shrink-0 space-y-4">
                  {/* Sub-mode Toggle */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Bootstrap Type</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant={bootstrapSubMode === 'product-brand' ? 'default' : 'outline'}
                        className={bootstrapSubMode === 'product-brand' ? 'bg-brand-primary hover:bg-brand-primary/90' : ''}
                        onClick={() => setBootstrapSubMode('product-brand')}
                        disabled={bootstrapStatus === 'searching' || bootstrapStatus === 'writing'}
                      >
                        <Package className="mr-1 h-3 w-3" />
                        Product Brand
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={bootstrapSubMode === 'venue-brand' ? 'default' : 'outline'}
                        className={bootstrapSubMode === 'venue-brand' ? 'bg-brand-primary hover:bg-brand-primary/90' : ''}
                        onClick={() => setBootstrapSubMode('venue-brand')}
                        disabled={bootstrapStatus === 'searching' || bootstrapStatus === 'writing'}
                      >
                        <Building2 className="mr-1 h-3 w-3" />
                        Venue Brand
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {bootstrapSubMode === 'product-brand' 
                        ? 'e.g., Lucozade, Redbull, Oats So Good'
                        : 'e.g., David Lloyd, Snap Fitness, Butlins'}
                    </p>
                  </div>

                  {/* Brand Name Input */}
                  <div className="space-y-1">
                    <Label htmlFor="bootstrapBrand" className="text-sm">Brand Name</Label>
                    <Input
                      id="bootstrapBrand"
                      placeholder={bootstrapSubMode === 'product-brand' ? 'e.g., Lucozade' : 'e.g., David Lloyd'}
                      value={bootstrapBrandName}
                      onChange={(e) => setBootstrapBrandName(e.target.value)}
                      disabled={bootstrapStatus === 'searching' || bootstrapStatus === 'writing'}
                    />
                  </div>

                  {/* Product Limit Slider */}
                  <div className="space-y-1">
                    <Label className="text-sm">Products to find: {bootstrapProductLimit}</Label>
                    <input
                      type="range"
                      min="1"
                      max="20"
                      value={bootstrapProductLimit}
                      onChange={(e) => setBootstrapProductLimit(parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-brand-primary"
                      disabled={bootstrapStatus === 'searching' || bootstrapStatus === 'writing'}
                    />
                    <p className="text-xs text-muted-foreground">1-20 products per search</p>
                  </div>

                  {bootstrapStatus === 'error' && (
                    <Alert className="bg-red-50 border-red-200 py-2">
                      <AlertDescription className="text-red-800 text-sm">
                        {bootstrapMessage}
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Progress Bar (shown during searching/writing) */}
                  {(bootstrapStatus === 'searching' || bootstrapStatus === 'writing') && (
                    <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin text-brand-primary" />
                        <span className="text-sm font-medium">
                          {bootstrapStatus === 'searching' ? 'Discovering...' : 'Writing to Sheets...'}
                        </span>
                      </div>
                      <Progress value={bootstrapProgress} className="h-2" />
                      <p className="text-xs text-muted-foreground">{bootstrapMessage}</p>
                    </div>
                  )}

                  {bootstrapStatus === 'completed' && (
                    <Alert className="bg-green-50 border-green-200 py-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <AlertDescription className="text-green-800 text-sm">
                        {bootstrapMessage}
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Action Buttons */}
                  <div className="space-y-2 pt-2">
                    <Button 
                      onClick={handleBootstrapDiscover}
                      disabled={!bootstrapBrandName.trim() || bootstrapStatus === 'searching' || bootstrapStatus === 'writing'}
                      className="w-full bg-brand-primary hover:bg-brand-primary/90"
                    >
                      {bootstrapStatus === 'searching' ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Searching...
                        </>
                      ) : (
                        <>
                          <Search className="mr-2 h-4 w-4" />
                          {bootstrapProducts.length > 0 ? 'Add More Products' : 'Discover Products'}
                        </>
                      )}
                    </Button>
                    
                    {bootstrapProducts.length > 0 && (
                      <>
                        <Button 
                          onClick={handleBootstrapWrite}
                          disabled={bootstrapStatus === 'searching' || bootstrapStatus === 'writing'}
                          className="w-full"
                          variant="outline"
                        >
                          {bootstrapStatus === 'writing' ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Writing...
                            </>
                          ) : (
                            <>
                              <Download className="mr-2 h-4 w-4" />
                              Send {bootstrapProducts.length} to Sheets
                            </>
                          )}
                        </Button>
                        <Button 
                          variant="ghost"
                          size="sm"
                          onClick={handleBootstrapReset}
                          disabled={bootstrapStatus === 'searching' || bootstrapStatus === 'writing'}
                          className="w-full text-muted-foreground"
                        >
                          <Trash2 className="mr-2 h-3 w-3" />
                          Clear All
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* Right Column: Product Preview */}
                <div className="flex-1 min-w-0">
                  <div className="border rounded-lg h-[500px] flex flex-col">
                    {/* Header */}
                    <div className="bg-slate-100 px-4 py-2 border-b flex items-center justify-between flex-shrink-0">
                      <div className="flex items-center gap-3">
                        <h4 className="font-medium text-sm">Product Preview</h4>
                        <span className="text-xs bg-brand-primary/10 text-brand-primary px-2 py-0.5 rounded-full">
                          {bootstrapProducts.length} products
                        </span>
                      </div>
                      {bootstrapProducts.length > 0 && (
                        <Button 
                          className="bg-brand-primary hover:bg-brand-primary/90 text-sm"
                          onClick={() => setMode('ai-content')}
                        >
                          Send to AI Content Generator
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    
                    {/* Product List */}
                    <div className="flex-1 overflow-y-auto">
                      {bootstrapProducts.length === 0 ? (
                        <div className="h-full flex items-center justify-center text-muted-foreground">
                          <div className="text-center space-y-2">
                            <Package className="mx-auto h-12 w-12 opacity-30" />
                            <p className="text-sm">No products discovered yet</p>
                            <p className="text-xs">Enter a brand name and click Discover</p>
                          </div>
                        </div>
                      ) : (
                        <div className="divide-y">
                          {bootstrapProducts.map((product: any, index: number) => (
                            <div key={product._id || `product-${index}`} className="p-3 flex items-center gap-3 hover:bg-slate-50 group">
                              {/* Product Image - Click to open Google Drive Picker */}
                              <div className="flex-shrink-0 relative">
                                {/* Hidden picker that auto-opens when clicked */}
                                {productImagePickerIndex === index && (
                                  <div className="absolute inset-0 opacity-0 pointer-events-none">
                                    <GoogleDrivePicker
                                      maxFiles={1}
                                      label=""
                                      hideButton={true}
                                      autoOpen={true}
                                      onFilesSelected={(result) => {
                                        if (result.files.length > 0) {
                                          handleProductImageFromDrive(index, result.files[0].id, result.accessToken);
                                        } else {
                                          setProductImagePickerIndex(null);
                                        }
                                      }}
                                    />
                                  </div>
                                )}
                                <div 
                                  className="relative cursor-pointer group/image"
                                  onClick={() => setProductImagePickerIndex(index)}
                                >
                                  {product.imageUrl ? (
                                    <>
                                      <img 
                                        src={product.imageUrl} 
                                        alt={product.name}
                                        className="w-14 h-14 object-cover rounded border"
                                        onError={(e) => {
                                          (e.target as HTMLImageElement).style.display = 'none';
                                          (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                                        }}
                                      />
                                      <div className="hidden w-14 h-14 bg-slate-200 rounded border flex items-center justify-center">
                                        <Upload className="h-5 w-5 text-slate-400" />
                                      </div>
                                      <div className="absolute inset-0 bg-black/50 rounded opacity-0 group-hover/image:opacity-100 transition-opacity flex items-center justify-center">
                                        <Upload className="h-5 w-5 text-white" />
                                      </div>
                                    </>
                                  ) : (
                                    <div className="w-14 h-14 bg-slate-200 rounded border flex items-center justify-center hover:bg-slate-300 transition-colors">
                                      <Upload className="h-5 w-5 text-slate-400" />
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Product Info */}
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm truncate">{product.name}</div>
                                <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                                  {product.category && (
                                    <span className="bg-slate-100 px-1.5 py-0.5 rounded">{product.category}</span>
                                  )}
                                  {product.size && <span>{product.size}</span>}
                                </div>
                              </div>
                              
                              {/* Per-Product Colors */}
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <div 
                                  className="w-6 h-6 rounded border shadow-sm" 
                                  style={{ backgroundColor: product.primaryColor || '#cccccc' }}
                                  title={`Primary: ${product.primaryColor || 'Not set'}`}
                                />
                                <div 
                                  className="w-6 h-6 rounded border shadow-sm" 
                                  style={{ backgroundColor: product.secondaryColor || '#eeeeee' }}
                                  title={`Secondary: ${product.secondaryColor || 'Not set'}`}
                                />
                              </div>
                              
                              {/* Delete Button */}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveBootstrapProduct(index)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                disabled={bootstrapStatus === 'searching' || bootstrapStatus === 'writing'}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (mode === 'sales-demos' || mode === 'live-deployment') ? (
          <Card className="w-full shadow-sm border-0 bg-white">
            <CardHeader className="bg-slate-50/50">
              <CardTitle className="text-brand-black flex items-center gap-2">
                {mode === 'sales-demos' ? (
                  <Presentation className="h-5 w-5 text-brand-primary" />
                ) : (
                  <Rocket className="h-5 w-5 text-brand-primary" />
                )}
                Product Information
              </CardTitle>
              <CardDescription>
                {mode === 'sales-demos' 
                  ? 'AI Agent fills in empty fields - uses consistent formatting across all products'
                  : 'Only UK Supermarket and brand sources - no additional agent'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {workflowStatus === 'running' || workflowStatus === 'completed' ? (
                <div className="space-y-6 py-4">
                  <div className="text-center space-y-2">
                    {workflowStatus === 'running' ? (
                      <Loader2 className="mx-auto h-12 w-12 animate-spin text-brand-primary" />
                    ) : (
                      <CheckCircle2 className="mx-auto h-12 w-12 text-green-500" />
                    )}
                    <h3 className="text-lg font-semibold">
                      {workflowStatus === 'running' ? 'Processing Market Research' : 'Workflow Complete!'}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Campaign: {campaign}
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Progress value={workflowProgress} className="h-3" />
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>{workflowMessage}</span>
                      <span>{Math.round(workflowProgress)}%</span>
                    </div>
                  </div>

                  {workflowStatus === 'completed' && (
                    <div className="space-y-4">
                      <Alert className="bg-green-50 border-green-200">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <AlertDescription className="text-green-800">
                          {workflowMessage || 'Product data has been added to Google Sheets!'}
                        </AlertDescription>
                      </Alert>

                      {workflowResults && workflowResults.length > 0 && (
                        <Card className="bg-slate-50">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base">Product Results</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            {workflowResults.map((result: any, index: number) => (
                              <div key={index} className="border-b pb-3 last:border-b-0 last:pb-0">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="font-medium text-sm">
                                      {result.product}{result.size ? `, ${result.size}` : ''}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1">
                                      {result.duplicate || result.source === 'existing_sheet' ? (
                                        <span className="text-blue-600">✓ {result.foundVia || `Copied from ${result.sheetName} sheet row ${result.rowNumber}`}</span>
                                      ) : (
                                        <span className="text-green-600">✓ Found via Tavily search and AI agent extraction</span>
                                      )}
                                    </div>
                                    {/* Column-level source information */}
                                    {result.columnSources && result.columnSources.length > 0 && (
                                      <div className="mt-2 space-y-1">
                                        <div className="text-xs font-medium text-muted-foreground mb-1">Column Sources:</div>
                                        {result.columnSources.map((source: any, sourceIndex: number) => (
                                          <div key={sourceIndex} className="text-xs pl-2">
                                            <span className="font-medium">{source.column}:</span>{' '}
                                            {source.source === 'verified' ? (
                                              <span className="text-green-600">✓ Found via approved sources</span>
                                            ) : source.source === 'ai_filler' ? (
                                              <span className="text-blue-600">AI Agent Filler</span>
                                            ) : (
                                              <span className="text-gray-600">Manual input</span>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </CardContent>
                        </Card>
                      )}

                      <Button
                        variant="outline"
                        onClick={() => {
                          setWorkflowStatus('idle');
                          setWorkflowProgress(0);
                          setWorkflowResult(null);
                          setWorkflowResults([]);
                          setCampaign('');
                          setProducts([{ featureDisplayName: '', size: '', driveFileIds: [] }]);
                        }}
                        className="w-full"
                      >
                        Research More Products
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <form onSubmit={handleMarketResearchSubmit} className="space-y-6">
                  {/* Campaign Input at Top */}
                  <div className="space-y-2">
                    <Label htmlFor="campaign" className="font-bold text-base">Campaign Name</Label>
                    <Input
                      id="campaign"
                      placeholder="e.g., David Lloyd"
                      value={campaign}
                      onChange={(e) => {
                        setCampaign(e.target.value);
                        if (formErrors.campaign) {
                          setFormErrors({ ...formErrors, campaign: undefined });
                        }
                      }}
                      required
                      className={formErrors.campaign ? 'border-red-500' : ''}
                    />
                    {formErrors.campaign && (
                      <p className="text-sm text-red-500">{formErrors.campaign}</p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      A new sheet will be created for this campaign if it doesn't exist.
                    </p>
                  </div>

                  {/* Products List Below */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="font-bold text-base">Products</Label>
                        <p className="text-sm text-muted-foreground">
                          Add products with Feature Display Name and Size. Each product will be processed in parallel.
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addProductRow}
                        className="h-8"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Product
                      </Button>
                    </div>

                    {formErrors.products && (
                      <Alert variant="destructive">
                        <AlertDescription>{formErrors.products}</AlertDescription>
                      </Alert>
                    )}

                    <div className="border rounded-lg overflow-hidden">
                      <div className="grid grid-cols-12 gap-2 p-3 bg-muted font-medium text-sm">
                        <div className="col-span-4">Feature Display Name</div>
                        <div className="col-span-2">Size</div>
                        <div className="col-span-5">
                          <div>Product Images (up to 5 images)</div>
                          <div className="text-xs font-normal text-muted-foreground">
                            1: Product Image • 2: Left • 3: Right • 4: Top • 5: Banner
                          </div>
                        </div>
                        <div className="col-span-1"></div>
                      </div>
                      <div className="divide-y">
                        {products.map((product, index) => (
                          <div key={index} className="grid grid-cols-12 gap-2 p-3 items-start">
                            <div className="col-span-4">
                              <Input
                                placeholder="e.g., Orange Lucozade Energy"
                                value={product.featureDisplayName}
                                onChange={(e) => updateProduct(index, 'featureDisplayName', e.target.value)}
                                className="h-9"
                              />
                            </div>
                            <div className="col-span-2">
                              <Input
                                placeholder="e.g., 300mL"
                                value={product.size}
                                onChange={(e) => updateProduct(index, 'size', e.target.value)}
                                className="h-9"
                              />
                            </div>
                            <div className="col-span-5">
                              <GoogleDrivePicker
                                onFilesSelected={(files) => updateProductImages(index, files)}
                                maxFiles={5}
                                currentFiles={product.driveFileIds}
                                label="Select Images"
                              />
                            </div>
                            <div className="col-span-1 flex justify-end pt-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeProductRow(index)}
                                disabled={products.length === 1}
                                className="h-8 w-8 p-0"
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Each product will be processed in parallel (up to 10 at a time). Duplicate products will be detected automatically.
                    </p>
                  </div>

                  <CardFooter className="px-0">
                    <Button 
                      type="submit" 
                      disabled={
                        isSubmitting || 
                        !campaign.trim() || 
                        products.filter(p => p.featureDisplayName.trim() && p.size.trim()).length === 0
                      } 
                      className="w-full bg-brand-primary hover:bg-brand-primary/90 text-white font-semibold py-6 text-base shadow-lg shadow-brand-primary/25"
                    >
                      <Play className="mr-2 h-5 w-5" />
                      Trigger Market Research Workflow
                    </Button>
                  </CardFooter>
                </form>
              )}
            </CardContent>
          </Card>
        ) : mode === 'ai-content' ? (
          <Card className="w-full shadow-sm border-0 bg-white">
            <CardHeader className="bg-slate-50/50">
              <CardTitle className="text-brand-black flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-brand-primary" />
                AI Content Generator
              </CardTitle>
              <CardDescription>
                Generate campaign images (Left, Right, Top, Banner) for each product
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="flex gap-6">
                {/* Left Column: Controls */}
                <div className="w-72 flex-shrink-0 space-y-4">
                  <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                    <h4 className="font-medium text-sm">How it works</h4>
                    <ol className="text-xs text-muted-foreground space-y-2 list-decimal list-inside">
                      <li>Add products via Brand Bootstrapping</li>
                      <li>Click "Generate All Campaign Media"</li>
                      <li>AI creates Left, Right, Top, Banner images</li>
                      <li>Click "Save to Sheets" to push images</li>
                    </ol>
                  </div>
                  
                  <Alert className="bg-brand-primary/5 border-brand-primary/20">
                    <Sparkles className="h-4 w-4 text-brand-primary" />
                    <AlertDescription className="text-sm">
                      <strong>{bootstrapProducts.length}</strong> products ready for image generation
                    </AlertDescription>
                  </Alert>

                  {/* Cost Warning */}
                  <Alert className="bg-amber-50 border-amber-200">
                    <AlertDescription className="text-xs text-amber-800">
                      <strong>Note:</strong> Each generation uses AI tokens which incur costs. 
                      If you need to edit or regenerate, consider using your own AI tools 
                      or editing images manually to avoid additional charges.
                    </AlertDescription>
                  </Alert>

                  {bootstrapProducts.length === 0 && (
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => setMode('brand-bootstrapping')}
                    >
                      <ArrowRight className="mr-2 h-4 w-4 rotate-180" />
                      Go to Brand Bootstrapping
                    </Button>
                  )}
                </div>

                {/* Right Column: Product List with Generate Buttons */}
                <div className="flex-1 min-w-0">
                  <div className="border rounded-lg h-[500px] flex flex-col">
                    {/* Header */}
                    <div className="bg-slate-100 px-4 py-2 border-b flex items-center justify-between flex-shrink-0">
                      <div className="flex items-center gap-3">
                        <h4 className="font-medium text-sm">Products for Generation</h4>
                        <span className="text-xs bg-brand-primary/10 text-brand-primary px-2 py-0.5 rounded-full">
                          {bootstrapProducts.length} products
                        </span>
                      </div>
                    </div>
                    
                    {/* Product List */}
                    <div className="flex-1 overflow-y-auto">
                      {bootstrapProducts.length === 0 ? (
                        <div className="h-full flex items-center justify-center text-muted-foreground">
                          <div className="text-center space-y-2">
                            <ImageIcon className="mx-auto h-12 w-12 opacity-30" />
                            <p className="text-sm">No products available</p>
                            <p className="text-xs">Use Brand Bootstrapping to add products first</p>
                          </div>
                        </div>
                      ) : (
                        <div className="divide-y">
                          {bootstrapProducts.map((product: any, index: number) => (
                            <div key={product._id || `ai-product-${index}`} className="p-4 space-y-3">
                              {/* Product Header Row */}
                              <div className="flex items-center gap-3">
                                {/* Product Image */}
                                {product.imageUrl ? (
                                  <img 
                                    src={product.imageUrl} 
                                    alt={product.name}
                                    className="w-16 h-16 object-cover rounded border flex-shrink-0"
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <div className="w-16 h-16 bg-slate-200 rounded border flex items-center justify-center flex-shrink-0">
                                    <Package className="h-6 w-6 text-slate-400" />
                                  </div>
                                )}
                                
                                {/* Product Info */}
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-sm truncate">{product.name}</div>
                                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                                    {product.category && (
                                      <span className="bg-slate-100 px-1.5 py-0.5 rounded">{product.category}</span>
                                    )}
                                    {product.size && <span>{product.size}</span>}
                                  </div>
                                </div>

                                {/* Per-Product Colors */}
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <div 
                                    className="w-5 h-5 rounded border shadow-sm" 
                                    style={{ backgroundColor: product.primaryColor || '#cccccc' }}
                                    title={`Primary: ${product.primaryColor || 'Not set'}`}
                                  />
                                  <div 
                                    className="w-5 h-5 rounded border shadow-sm" 
                                    style={{ backgroundColor: product.secondaryColor || '#eeeeee' }}
                                    title={`Secondary: ${product.secondaryColor || 'Not set'}`}
                                  />
                                </div>
                              </div>

                              {/* Progress Bar (when generating) */}
                              {productGenerationStates[index]?.isGenerating && (
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between text-xs">
                                    <span className="text-muted-foreground">
                                      Generating {productGenerationStates[index].currentStep}... ({productGenerationStates[index].stepNumber}/4)
                                    </span>
                                    <span className="text-brand-primary font-medium">
                                      {Math.round(productGenerationStates[index].progress)}%
                                    </span>
                                  </div>
                                  <Progress value={productGenerationStates[index].progress} className="h-2" />
                                </div>
                              )}

                              {/* Generated Images Display */}
                              {(productGenerationStates[index]?.generatedImages && 
                                Object.keys(productGenerationStates[index].generatedImages).length > 0) && (
                                <div className="space-y-2">
                                  <div className="text-xs font-medium text-muted-foreground">Generated Images:</div>
                                  <div className="grid grid-cols-4 gap-2">
                                    {['left', 'right', 'top', 'banner'].map((position) => {
                                      const imgUrl = productGenerationStates[index]?.generatedImages[position as keyof typeof productGenerationStates[typeof index]['generatedImages']];
                                      return (
                                        <div key={position} className="space-y-1">
                                          <div className="text-xs text-center text-muted-foreground capitalize">{position}</div>
                                          {imgUrl ? (
                                            <img 
                                              src={imgUrl} 
                                              alt={`${position} media`}
                                              className="w-full aspect-square object-cover rounded border"
                                            />
                                          ) : (
                                            <div className="w-full aspect-square bg-slate-100 rounded border flex items-center justify-center">
                                              {productGenerationStates[index]?.isGenerating ? (
                                                <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                                              ) : (
                                                <ImageIcon className="h-4 w-4 text-slate-300" />
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Generate All Button */}
                              <div className="flex items-center gap-2">
                                <Button 
                                  variant="default" 
                                  size="sm" 
                                  className="text-xs bg-brand-primary hover:bg-brand-primary/90"
                                  disabled={!product.imageUrl || productGenerationStates[index]?.isGenerating}
                                  onClick={() => handleGenerateCampaignMedia(index)}
                                >
                                  {productGenerationStates[index]?.isGenerating ? (
                                    <>
                                      <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                                      Generating...
                                    </>
                                  ) : (
                                    <>
                                      <Sparkles className="mr-1.5 h-3 w-3" />
                                      Generate All Campaign Media
                                    </>
                                  )}
                                </Button>
                                
                                {/* Save to Sheets button - appears after generation */}
                                {productGenerationStates[index]?.progress === 100 && !productGenerationStates[index]?.isGenerating && (
                                  productGenerationStates[index]?.savedToSheets ? (
                                    <span className="text-xs text-green-600 flex items-center gap-1">
                                      <CheckCircle2 className="h-3 w-3" />
                                      Saved to Sheets
                                    </span>
                                  ) : (
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      className="text-xs border-green-600 text-green-600 hover:bg-green-50"
                                      onClick={() => handlePushImagesToSheets(index)}
                                    >
                                      <CheckCircle2 className="mr-1.5 h-3 w-3" />
                                      Save to Sheets
                                    </Button>
                                  )
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </DashboardLayout>
  );
}
